/**
 * AttachedFiles Component
 *
 * Displays attached files as context for AI queries.
 * Shows file names, sizes, and allows removal.
 */

import { FileText, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FileContext, formatBytes, MAX_TOTAL_CONTEXT_SIZE } from '@/lib/hooks/useFileContext';

export interface AttachedFilesProps {
  /** List of attached files */
  files: FileContext[];
  /** Total size of all files */
  totalSize: number;
  /** Callback to remove a file */
  onRemove: (id: string) => void;
  /** Callback to clear all files */
  onClearAll?: () => void;
  /** Whether to show compact view */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Display attached files with remove buttons
 */
export function AttachedFiles({
  files,
  totalSize,
  onRemove,
  onClearAll,
  compact = false,
  className,
}: AttachedFilesProps) {
  if (files.length === 0) return null;

  const usagePercent = Math.round((totalSize / MAX_TOTAL_CONTEXT_SIZE) * 100);

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header with total size */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {files.length} file{files.length !== 1 ? 's' : ''} attached ({formatBytes(totalSize)} / {formatBytes(MAX_TOTAL_CONTEXT_SIZE)})
        </span>
        {files.length > 1 && onClearAll && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onClearAll}
          >
            Clear all
          </Button>
        )}
      </div>

      {/* Size indicator bar */}
      <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full transition-all',
            usagePercent > 80 ? 'bg-orange-500' : 'bg-primary'
          )}
          style={{ width: `${Math.min(usagePercent, 100)}%` }}
        />
      </div>

      {/* File list */}
      <div className={cn(
        'flex flex-wrap gap-2',
        compact && 'gap-1'
      )}>
        {files.map((file) => (
          <div
            key={file.id}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50 border border-border/50',
              compact ? 'text-xs' : 'text-sm'
            )}
            title={`${file.name} (${formatBytes(file.size)}) - Click X to remove`}
          >
            <FileText className={cn(
              'text-muted-foreground flex-shrink-0',
              compact ? 'h-3 w-3' : 'h-4 w-4'
            )} />
            <span className="truncate max-w-[120px] sm:max-w-[180px]">
              {file.name}
            </span>
            <span className="text-muted-foreground text-xs">
              ({formatBytes(file.size)})
            </span>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'flex-shrink-0 hover:bg-destructive/10 hover:text-destructive',
                compact ? 'h-4 w-4' : 'h-5 w-5'
              )}
              onClick={() => onRemove(file.id)}
            >
              <X className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AttachedFiles;
